import axios from 'axios'
import type { InternalAxiosRequestConfig } from 'axios'
import { setAuthToken, clearAuthToken } from './tokenRefresh'

// All traffic goes through the gateway (same pattern as admin app)
const GATEWAY_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

export const api = axios.create({
  baseURL: GATEWAY_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

export const contentApi = axios.create({
  baseURL: GATEWAY_URL,
  timeout: 30000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
})

export const marketplaceApi = axios.create({
  baseURL: GATEWAY_URL,
  timeout: 30000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
})

export const assistantApi = axios.create({
  baseURL: GATEWAY_URL,
  timeout: 300000, // 5min — alinhado com nginx (proxy_read_timeout=300s)
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
})

// ---------------------------------------------------------------------------
// Shared refresh state — single lock across all API instances.
// Without this, concurrent 401s from different instances would trigger
// multiple simultaneous refresh calls.
// ---------------------------------------------------------------------------
let isRefreshing = false
type Subscriber = { resolve: (token: string) => void; reject: (err: unknown) => void }
let refreshSubscribers: Subscriber[] = []

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(sub => sub.resolve(token))
  refreshSubscribers = []
}

const onRefreshFailed = (error: unknown) => {
  refreshSubscribers.forEach(sub => sub.reject(error))
  refreshSubscribers = []
}

const redirectToLogin = () => {
  clearAuthToken()
  // Best-effort server-side revocation (cookie sent automatically)
  axios.post(`${GATEWAY_URL}/auth/logout`, {}, { withCredentials: true }).catch(() => {})
  // replace() prevents the login page from appearing in browser history
  window.location.replace('/login')
}

const addInterceptors = (apiInstance: ReturnType<typeof axios.create>) => {
  apiInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  apiInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      if (error.response?.status === 401 && !originalRequest._retry) {
        const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password'].includes(window.location.pathname)
        if (isAuthPage) return Promise.reject(error)

        // Se não há access token, usuário nunca logou — não tenta refresh
        if (!localStorage.getItem('auth_token')) {
          redirectToLogin()
          return Promise.reject(error)
        }

        if (isRefreshing) {
          // Queue request — will be retried once refresh completes or rejected if it fails
          return new Promise((resolve, reject) => {
            refreshSubscribers.push({
              resolve: (token: string) => {
                originalRequest.headers.Authorization = `Bearer ${token}`
                resolve(apiInstance(originalRequest))
              },
              reject,
            })
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const { data } = await axios.post(`${GATEWAY_URL}/auth/refresh`, {}, { withCredentials: true })
          const newAccessToken = data.tokens?.accessToken || data.token

          if (!newAccessToken) {
            throw new Error('Refresh response missing access token')
          }

          setAuthToken(newAccessToken)
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
          onRefreshed(newAccessToken)
          return apiInstance(originalRequest)
        } catch (refreshError) {
          // Reject all queued requests and force re-login
          onRefreshFailed(refreshError)
          redirectToLogin()
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      return Promise.reject(error)
    }
  )
}

addInterceptors(api)
addInterceptors(contentApi)
addInterceptors(marketplaceApi)
addInterceptors(assistantApi)
